import React, { useState, useEffect, useRef } from 'react';
import SunEditor from 'suneditor-react';
import 'suneditor/dist/css/suneditor.min.css';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import './NewOrderCreate.css';

const NewOrderCreate = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // UI & Loading States
    const [loading, setLoading] = useState(false);
    const [isPreview, setIsPreview] = useState(false);

    // Document States
    const [orderId, setOrderId] = useState(id && id !== 'create' ? id : null);
    const [title, setTitle] = useState('Бұйрық тақырыбын осы жерге жазыңыз...');
    const [htmlContent, setHtmlContent] = useState('');
    const [orderNumber, setOrderNumber] = useState('');
    const [status, setStatus] = useState('draft');
    const [creatorId, setCreatorId] = useState(null);
    const [attachments, setAttachments] = useState([]);

    // Workflow & Signature States
    const [allUsers, setAllUsers] = useState([]);
    const [selectedApprover, setSelectedApprover] = useState(null);
    const [selectedSigners, setSelectedSigners] = useState([]);
    const [selectedExecutors, setSelectedExecutors] = useState(null);

    // ЭЦП States
    const [canSign, setCanSign] = useState(false);
    const [isAlreadySigned, setIsAlreadySigned] = useState(false);

    // Signatures raw data (for preview)
    const [signatures, setSignatures] = useState([]);

    const currentUserId = localStorage.getItem('user_id');
    const creatingRef = useRef(false);

    const getAuthHeaders = (isMultipart = false) => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': isMultipart ? 'multipart/form-data' : 'application/json',
        },
    });

    // 1. Мәліметтерді жүктеу
    useEffect(() => {
        const initPage = async () => {
            try {
                const config = getAuthHeaders();
                const usersRes = await axios.get('https://expertise.gov.kz/api/orders/users/list', config);
                setAllUsers(usersRes.data);

                if (id && id !== 'create') {
                    const res = await axios.get(`https://expertise.gov.kz/api/orders/${id}`, config);
                    const data = res.data;
                    setOrderId(data.id);
                    setTitle(data.title || '');
                    setHtmlContent(data.html_content || '');
                    setOrderNumber(data.order_number || '');
                    setStatus(data.status);
                    setCreatorId(data.creator_id);
                    setAttachments(data.attachments || []);

                    if (data.signatures) {
                        setSignatures(data.signatures);
                        const approver = data.signatures.find(s => s.signature_type === 'approver');
                        if (approver) setSelectedApprover(approver.user);

                        const signers = data.signatures
                            .filter(s => s.signature_type === 'signer')
                            .map(s => s.user);
                        setSelectedSigners(signers);

                        const executor = data.signatures.find(s => s.signature_type === 'executor');
                        if (executor) setSelectedExecutors(executor.user);

                        const mySig = data.signatures.find(
                            s => String(s.user_id) === String(currentUserId)
                        );
                        if (mySig) {
                            setIsAlreadySigned(mySig.is_signed === 1 || mySig.is_signed === true);
                            const previousOnes = data.signatures.filter(
                                s => s.signing_order < mySig.signing_order
                            );
                            const allPreviousSigned = previousOnes.every(
                                s => s.is_signed === 1 || s.is_signed === true
                            );
                            if (data.status === 'signing' && !mySig.is_signed && allPreviousSigned) {
                                setCanSign(true);
                            } else {
                                setCanSign(false);
                            }
                        }
                    }
                } else {
                    if (creatingRef.current) return;
                    creatingRef.current = true;
                    const orderRes = await axios.post(
                        'https://expertise.gov.kz/api/orders',
                        { title: 'Жаңа бұйрық' },
                        config
                    );
                    const newId = orderRes.data.order_id;
                    setOrderId(newId);
                    setCreatorId(currentUserId);
                    navigate(`/staff-groups/new-order/${newId}`, { replace: true });
                }
            } catch (err) {
                console.error('Жүктеу қатесі', err);
            }
        };
        initPage();
    }, [id, navigate, status]);

    // ЭЦП логикасы
    const handleSignDocument = async () => {
        if (!window.NCA) {
            return alert('❌ ncalayer-utils.js жүктелмеген. Бетті жаңартып көріңіз.');
        }
        setLoading(true);
        try {
            const config = getAuthHeaders();
            const isConnected = await window.NCA.connect();
            if (!isConnected) {
                alert('❌ NCALayer іске қосылмаған. Бағдарламаның қосулы екенін тексеріңіз.');
                setLoading(false);
                return;
            }
            const resBase64 = await axios.get(
                `https://expertise.gov.kz/api/orders/${orderId}/pdf-base64`,
                config
            );
            const base64ToSign = resBase64.data.base64;

            window.NCA.createCMSSignatureFromBase64(
                'PKCS12',
                'SIGNATURE',
                base64ToSign,
                true,
                async res => {
                    if (res.getCode() === '200') {
                        const signedCMS = res.getResponseObject();
                        try {
                            await axios.post(
                                `https://expertise.gov.kz/api/orders/${orderId}/sign`,
                                { signed_pdf_base64: signedCMS },
                                config
                            );
                            alert('✅ Құжатқа сәтті қол қойылды!');
                            window.location.reload();
                        } catch (e) {
                            alert(
                                '❌ Тексеру қатесі: ' +
                                    (e.response?.data?.message || 'ЭЦП жарамсыз')
                            );
                        }
                    } else {
                        if (res.getCode() !== '100') {
                            alert('Қате: ' + res.getMessage());
                        }
                    }
                    setLoading(false);
                }
            );
        } catch (err) {
            alert('Жүйелік қате: ' + err.message);
            setLoading(false);
        }
    };

    const addUserToRole = (userId, list, setList, isSingle = false) => {
        const user = allUsers.find(u => String(u.id) === String(userId));
        if (!user) return;
        if (isSingle) {
            setList(user);
        } else {
            if (!list.find(item => String(item.id) === String(user.id))) {
                setList([...list, user]);
            }
        }
    };

    const handleFileUpload = async e => {
        const file = e.target.files[0];
        if (!file || !orderId) return;
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(
                `https://expertise.gov.kz/api/orders/${orderId}/upload-attachment`,
                formData,
                getAuthHeaders(true)
            );
            if (res.data?.attachment) {
                setAttachments(prev => [...prev, res.data.attachment]);
                alert('Файл сәтті тіркелді');
            }
        } catch {
            alert('Файл жүктеу қатесі');
        }
    };

    const handleSendToAgreement = async () => {
        if (!selectedApprover || selectedSigners.length === 0 || !selectedExecutors) {
            return alert('Директорды, келісушілерді және орындаушыны таңдаңыз!');
        }
        if (!htmlContent || htmlContent.trim() === '' || htmlContent === '<p><br></p>') {
            return alert('Бұйрық мәтінін жазыңыз!');
        }
        setLoading(true);
        const workflow = [
            {
                user_id: selectedApprover.id,
                role: 'approver',
                role_name: selectedApprover.position,
            },
            ...selectedSigners.map(s => ({
                user_id: s.id,
                role: 'signer',
                role_name: s.position,
            })),
            {
                user_id: selectedExecutors.id,
                role: 'executor',
                role_name: selectedExecutors.position,
            },
        ];
        try {
            await axios.post(
                `https://expertise.gov.kz/api/orders/${orderId}/update-content`,
                { title, html_content: htmlContent, workflow },
                getAuthHeaders()
            );
            setStatus('on_agreement');
            alert('Бұйрық сақталды және келісуге жіберілді.');
        } catch (err) {
            alert('Сақтау қатесі: ' + (err.response?.data?.message || 'Белгісіз қате'));
        } finally {
            setLoading(false);
        }
    };

    const handleFinalize = async () => {
        if (!orderNumber) return alert('Бұйрық нөмірін жазыңыз!');
        setLoading(true);
        try {
            const res = await axios.post(
                `https://expertise.gov.kz/api/orders/${orderId}/finalize`,
                { order_number: orderNumber },
                getAuthHeaders()
            );
            alert('Құжат тіркелді!');
            setStatus('signing');
            if (res.data.pdf_url) window.open(res.data.pdf_url, '_blank');
        } catch {
            alert('Тіркеу қатесі.');
        } finally {
            setLoading(false);
        }
    };

    const handleReturnToDraft = async () => {
        if (!window.confirm('Бұйрықты черновикке қайтарғыңыз келе ме?')) return;
        try {
            await axios.post(
                `https://expertise.gov.kz/api/orders/${orderId}/return-to-draft`,
                { type: 'revoke' },
                getAuthHeaders()
            );
            setStatus('draft');
            setOrderNumber('');
            alert('Құжат черновикке қайтарылды.');
        } catch {
            alert('Қайтару қатесі.');
        }
    };

    const isCreator = String(creatorId) === String(currentUserId);
    const isExecutor =
        selectedExecutors && String(selectedExecutors.id) === String(currentUserId);
    const canEdit = status === 'draft';

    /* ══════════════════════════════════════════════════
       PREVIEW MODE — A4 paper matching the PDF layout
    ══════════════════════════════════════════════════ */
    if (isPreview) {
        return (
            <div className="order-preview-container">
                <div className="order-preview-toolbar">
                    <button className="btn--preview-back" onClick={() => setIsPreview(false)}>
                        ✎ Редакциялауға қайту
                    </button>
                </div>

                <div className="order-paper-a4">
                    {/* Header */}
                    <div className="a4-header-outer">
                        <div className="a4-header-grid">
                            <div className="a4-org-kz">
                                Қазақстан Республикасы Оқу-ағарту министрлігі<br />
                                Білім саласында сапаны қамтамасыз ету комитетінің<br />
                                "Республикалық ғылыми-практикалық білім мазмұнын<br />
                                сараптау орталығы" РМҚК
                            </div>
                            <img
                                className="a4-coat"
                                src="/logo192.png"
                                alt="Герб"
                            />
                            <div className="a4-org-ru">
                                "Республиканский научно-практический центр<br />
                                экспертизы содержания образования"<br />
                                Комитета по обеспечению качества<br />
                                в сфере образования Министерства просвещения РК
                            </div>
                        </div>

                        <div className="a4-address-row">
                            <span>г. Астана, Республика, 24</span>
                            <span>г. Астана, Республика, 24</span>
                        </div>

                        <div className="a4-doc-titles">
                            <span className="a4-doc-title">БҰЙРЫҚ</span>
                            <span className="a4-doc-title">ПРИКАЗ</span>
                        </div>
                    </div>

                    {/* Meta row */}
                    <div className="a4-meta-row">
                        <span>№ {orderNumber || '___________'}</span>
                        <span>{new Date().toLocaleDateString('ru-RU')}</span>
                    </div>

                    {/* Subject */}
                    <div className="a4-subject">Тақырып: {title}</div>

                    {/* Body */}
                    <div
                        className="a4-content"
                        dangerouslySetInnerHTML={{ __html: htmlContent }}
                    />

                    {/* Signature block */}
                    {(() => {
                        const approverSig = signatures.find(s => s.signature_type === 'approver');
                        const isSigned = approverSig?.is_signed === 1 || approverSig?.is_signed === true;
                        const signedAt = approverSig?.signed_at
                            ? new Date(approverSig.signed_at).toLocaleDateString('ru-RU')
                            : null;
                        return (
                            <div className="a4-signature-block">
                                <div className="a4-signature-role">Руководство</div>
                                {isSigned ? (
                                    <div className="a4-signature-stamp">
                                        подписано<br />
                                        {signedAt}
                                    </div>
                                ) : (
                                    <div className="a4-signature-stamp a4-signature-stamp--pending">
                                        ожидает<br />подписания
                                    </div>
                                )}
                                <div>{selectedApprover?.name || ''}</div>
                            </div>
                        );
                    })()}

                    {/* Participants */}
                    {(selectedApprover || selectedSigners.length > 0 || selectedExecutors) && (
                        <div className="a4-participants">
                            <h4>Участники документа:</h4>

                            {selectedApprover && (() => {
                                const sig = signatures.find(s => s.signature_type === 'approver');
                                const signed = sig?.is_signed === 1 || sig?.is_signed === true;
                                const signedAt = sig?.signed_at
                                    ? new Date(sig.signed_at).toLocaleDateString('ru-RU') +
                                      ' ' +
                                      new Date(sig.signed_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                                    : null;
                                return (
                                    <div className="a4-participants-section">
                                        <strong>Утверждение</strong>
                                        <div className="a4-participants-row">
                                            {selectedApprover.name},{' '}
                                            {signed
                                                ? <span className="a4-signed-label">подписано, {signedAt}</span>
                                                : <span className="a4-pending-label">ожидает подписания</span>
                                            }
                                        </div>
                                    </div>
                                );
                            })()}

                            {selectedSigners.length > 0 && (
                                <div className="a4-participants-section">
                                    <strong>Согласование</strong>
                                    {selectedSigners.map(s => {
                                        const sig = signatures.find(
                                            sig => sig.signature_type === 'signer' &&
                                            String(sig.user_id) === String(s.id)
                                        );
                                        const signed = sig?.is_signed === 1 || sig?.is_signed === true;
                                        const signedAt = sig?.signed_at
                                            ? new Date(sig.signed_at).toLocaleDateString('ru-RU') +
                                              ' ' +
                                              new Date(sig.signed_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                                            : null;
                                        return (
                                            <div key={s.id} className="a4-participants-row">
                                                {s.name},{' '}
                                                {signed
                                                    ? <span className="a4-signed-label">согласовано, {signedAt}</span>
                                                    : <span className="a4-pending-label">ожидает согласования</span>
                                                }
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {selectedExecutors && (() => {
                                const sig = signatures.find(s => s.signature_type === 'executor');
                                const done = sig?.is_signed === 1 || sig?.is_signed === true;
                                const doneAt = sig?.signed_at
                                    ? new Date(sig.signed_at).toLocaleDateString('ru-RU') +
                                      ' ' +
                                      new Date(sig.signed_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                                    : null;
                                return (
                                    <div className="a4-participants-section">
                                        <strong>Исполнение</strong>
                                        <div className="a4-participants-row">
                                            {selectedExecutors.name},{' '}
                                            {done
                                                ? <span className="a4-signed-label">исполнено, {doneAt}</span>
                                                : <span className="a4-pending-label">не начато</span>
                                            }
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}
                </div>
            </div>
        );
    }

    /* ══════════════════════════════════════════════════
       MAIN EDITOR VIEW
    ══════════════════════════════════════════════════ */
    return (
        <div className="order-app-container">
            <div className="order-page-wrapper">

                {/* Top toolbar */}
                <div className="order-action-bar">
                    <div className="order-action-bar-left">
                        <button className="btn btn--back" onClick={() => navigate(-1)}>
                            ⬅ Артқа қайту
                        </button>
                        <button className="btn--preview" onClick={() => setIsPreview(true)}>
                            🖥 Предпросмотр
                        </button>
                    </div>
                    <div className="order-action-bar-right">
                        {status !== 'draft' && (isCreator || isExecutor) && (
                            <button className="btn btn--revoke" onClick={handleReturnToDraft}>
                                ↩️ Отозвать (Черновикке)
                            </button>
                        )}
                    </div>
                </div>

                {/* Status */}
                <div className="order-status-banner">
                    Ағымдағы статус:{' '}
                    <span className="order-status-badge">
                        {status.replace('_', ' ').toUpperCase()}
                    </span>
                </div>

                {/* Document card */}
                <div className="order-white-block">
                    {/* Document header — PDF style */}
                    <div className="order-header-outer">
                        <div className="order-header-grid">
                            <div className="order-org-kz">
                                Қазақстан Республикасы Оқу-ағарту министрлігі<br />
                                "Республикалық ғылыми-практикалық білім<br />
                                мазмұнын сараптау орталығы" РМҚК
                            </div>
                            <img
                                className="order-coat-of-arms"
                                src="/logo192.png"
                                alt="Герб"
                            />
                            <div className="order-org-ru">
                                "Республиканский научно-практический центр<br />
                                экспертизы содержания образования"<br />
                                Комитета по обеспечению качества РК
                            </div>
                        </div>

                        <div className="order-address-row">
                            <span>г. Астана, Республика, 24</span>
                            <span>г. Астана, Республика, 24</span>
                        </div>

                        <div className="order-doc-titles">
                            <span className="order-doc-title-kz">БҰЙРЫҚ</span>
                            <span className="order-doc-title-ru">ПРИКАЗ</span>
                        </div>
                    </div>

                    {/* Order number + date */}
                    <div className="order-meta-row">
                        <span>№ {orderNumber || '—'}</span>
                        <span>{new Date().toLocaleDateString('ru-RU')}</span>
                    </div>

                    {/* Title */}
                    <label className="order-label">Бұйрық тақырыбы:</label>
                    <input
                        type="text"
                        className="order-main-input"
                        value={title}
                        onChange={e => setTitle(e.target.value)}
                        disabled={!canEdit}
                    />

                    {/* Rich text editor */}
                    <div className="order-editor-wrapper">
                        <SunEditor
                            setContents={htmlContent}
                            onChange={setHtmlContent}
                            disable={!canEdit}
                            setOptions={{
                                width: '100%',
                                minHeight: '400px',
                                readOnly: !canEdit,
                                buttonList: canEdit
                                    ? [
                                          ['undo', 'redo'],
                                          ['font', 'fontSize'],
                                          ['bold', 'underline', 'italic'],
                                          ['align', 'list', 'table'],
                                          ['image', 'video'],
                                      ]
                                    : [],
                            }}
                        />
                    </div>
                </div>

                {/* Finalize block (executor only, on_agreement) */}
                {status === 'on_agreement' && isExecutor && (
                    <div className="order-white-block order-white-block--highlight">
                        <h4 className="order-section-title">
                            🚀 Тіркеу бөлімі (Исполнитель)
                        </h4>
                        <div className="order-finalize-row">
                            <div className="order-finalize-input-group">
                                <label className="order-label">Бұйрық нөмірі:</label>
                                <input
                                    type="text"
                                    placeholder="Мысалы: 125-п"
                                    className="order-main-input"
                                    value={orderNumber}
                                    onChange={e => setOrderNumber(e.target.value)}
                                />
                            </div>
                            <button
                                className="btn btn--finalize"
                                onClick={handleFinalize}
                                disabled={loading}
                            >
                                Тіркеу және PDF жасау
                            </button>
                        </div>
                    </div>
                )}

                {/* Workflow */}
                <div className="order-white-block">
                    <h4 className="order-section-title">Workflow:</h4>

                    {/* Approver */}
                    <div className="order-workflow-row">
                        <p className="order-workflow-heading">Утверждающий (Директор)</p>
                        <select
                            className="order-select"
                            disabled={!canEdit}
                            value={selectedApprover?.id || ''}
                            onChange={e =>
                                addUserToRole(e.target.value, null, setSelectedApprover, true)
                            }
                        >
                            <option value="">Таңдаңыз...</option>
                            {allUsers.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.name} ({u.position})
                                </option>
                            ))}
                        </select>
                        {selectedApprover && (
                            <div className="order-tags-row">
                                <span className="order-tag">{selectedApprover.name} ✅</span>
                            </div>
                        )}
                    </div>

                    {/* Signers */}
                    <div className="order-workflow-row">
                        <p className="order-workflow-heading">Согласующие</p>
                        <select
                            className="order-select"
                            disabled={!canEdit}
                            onChange={e =>
                                addUserToRole(e.target.value, selectedSigners, setSelectedSigners)
                            }
                        >
                            <option value="">Қосу...</option>
                            {allUsers.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </select>
                        <div className="order-tags-row">
                            {selectedSigners.map(
                                u =>
                                    u && (
                                        <span key={u.id} className="order-tag">
                                            {u.name}
                                            <span
                                                className="order-tag-remove"
                                                onClick={() =>
                                                    canEdit &&
                                                    setSelectedSigners(
                                                        selectedSigners.filter(
                                                            s => String(s.id) !== String(u.id)
                                                        )
                                                    )
                                                }
                                            >
                                                ×
                                            </span>
                                        </span>
                                    )
                            )}
                        </div>
                    </div>

                    {/* Executor */}
                    <div className="order-workflow-row">
                        <p className="order-workflow-heading">Исполнитель (Нөмір қоюшы)</p>
                        <select
                            className="order-select"
                            disabled={!canEdit}
                            value={selectedExecutors?.id || ''}
                            onChange={e =>
                                addUserToRole(
                                    e.target.value,
                                    null,
                                    setSelectedExecutors,
                                    true
                                )
                            }
                        >
                            <option value="">Таңдаңыз...</option>
                            {allUsers.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.name}
                                </option>
                            ))}
                        </select>
                        {selectedExecutors && (
                            <div className="order-tags-row">
                                <span className="order-tag">
                                    {selectedExecutors.name} ✍️
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Attachments */}
                <div className="order-white-block">
                    <h4 className="order-section-title">📎 Бұйрық қосымшалары:</h4>
                    {canEdit && (
                        <input
                            type="file"
                            className="order-file-input"
                            onChange={handleFileUpload}
                            accept="application/pdf"
                        />
                    )}
                    <div className="order-attachment-list">
                        {attachments.filter(Boolean).map((f, i) => (
                            <div key={i} className="order-attachment-item">
                                <span>📄 {f.file_name}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Bottom action buttons */}
                <div className="order-bottom-nav">
                    {canEdit && (
                        <button
                            className="btn btn--submit"
                            onClick={handleSendToAgreement}
                            disabled={loading}
                        >
                            🚀 Сақтау және келісуге жіберу
                        </button>
                    )}

                    {status === 'signing' && canSign && (
                        <div className="order-alert--signing">
                            <p className="order-signing-hint">📜 Сіздің кезегіңіз келді!</p>
                            <button
                                className="btn btn--sign"
                                onClick={handleSignDocument}
                                disabled={loading}
                            >
                                {loading ? 'Күтіңіз...' : '✍️ ЭЦП-мен қол қою'}
                            </button>
                        </div>
                    )}

                    {status === 'signing' && isAlreadySigned && (
                        <div className="order-alert order-alert--success">
                            ✅ Сіз бұл құжатқа қол қойып қойғансыз.
                        </div>
                    )}

                    {status === 'signing' && !canSign && !isAlreadySigned && (
                        <div className="order-alert order-alert--info">
                            ⌛ Кезегіңізді күтіңіз. Алдыңғы қатысушылар қол қоюы керек.
                        </div>
                    )}

                    {status === 'on_agreement' && !isExecutor && (
                        <div className="order-alert order-alert--info">
                            ⌛ Құжат орындаушының тіркеуін күтіп тұр...
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default NewOrderCreate;